/**
 * CSRF token for state-changing auth requests.
 * Token is fetched from API and sent in X-CSRF-Token header; stored in memory only.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const CSRF_ENDPOINT = `${API_BASE_URL}/auth/csrf`;

let cachedToken = null;
let cacheTime = 0;
const CACHE_MS = 50 * 60 * 1000; // 50 minutes (tokens valid 1h)

/**
 * Fetch a CSRF token from the API. Cached in memory for CACHE_MS.
 * @returns {Promise<string|null>} Token or null on failure
 */
export async function getCsrfToken() {
  if (cachedToken && Date.now() - cacheTime < CACHE_MS) {
    return cachedToken;
  }
  try {
    const res = await fetch(CSRF_ENDPOINT, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' }
    });
    if (!res.ok) return null;
    const data = await res.json();
    const token = data?.csrfToken || null;
    if (token) {
      cachedToken = token;
      cacheTime = Date.now();
    }
    return token;
  } catch {
    return null;
  }
}

/**
 * Build headers object with X-CSRF-Token if available. Does not fetch; use getCsrfToken() first.
 * @param {Record<string, string>} [baseHeaders={}]
 * @param {string|null} [token=null] - If null, uses cached token
 * @returns {Promise<Record<string, string>>}
 */
export async function headersWithCsrf(baseHeaders = {}, token = null) {
  const t = token !== null ? token : await getCsrfToken();
  const out = { ...baseHeaders };
  if (t) out['X-CSRF-Token'] = t;
  return out;
}

/**
 * Clear cached token (e.g. after logout).
 */
export function clearCsrfCache() {
  cachedToken = null;
  cacheTime = 0;
}
